// Sistema con transición REALMENTE suave entre vistas
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useOcupacionPanoramica } from '../../hooks/useEspacios'
import { formatLocalDateToString, parseLocalDateString } from '../../utils/dateHelpers'
import { filterEspaciosPublicos } from '../../utils/espacioFilters'
import { slugify } from '../../utils/stringHelpers'
import SimplifiedGoogleMap from './SimplifiedGoogleMap'
import CampingFloorPlan from '../spaces/CampingFloorPlan'
import LoadingSpinner from '../common/LoadingSpinner'
import type { EspacioDisponible } from '../../types'
import type { FiltrosDisponibilidad } from '../../types/availability'

interface TransitionMapSystemProps {
  className?: string
  height?: string
}

type RangoDisponibilidad = 'hoy' | 'semana' | 'mes' | 'rango'

const TransitionMapSystemWithAPI: React.FC<TransitionMapSystemProps> = ({
  className = '',
  height = '600px'
}) => {
  const navigate = useNavigate()
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const [currentView, setCurrentView] = useState<'google' | 'detailed'>('detailed')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [mostrarDisponibilidad, setMostrarDisponibilidad] = useState(false) // ✅ Checkbox control - por defecto NO seleccionado
  const [rangoSeleccionado, setRangoSeleccionado] = useState<RangoDisponibilidad>('hoy')

  // Estados para fechas personalizadas (chip "Un rango")
  const [fechaInicioPersonalizada, setFechaInicioPersonalizada] = useState('')
  const [fechaFinPersonalizada, setFechaFinPersonalizada] = useState('')

  // ✅ Calcular fechas según el rango seleccionado (SIEMPRE)
  const filtrosDisponibilidad = useMemo<FiltrosDisponibilidad | null>(() => {
    const hoy = new Date()
    let fechaEntrada: Date
    let fechaSalida: Date

    switch (rangoSeleccionado) {
      case 'hoy':
        fechaEntrada = hoy
        fechaSalida = hoy
        break
      case 'semana':
        fechaEntrada = hoy
        fechaSalida = new Date(hoy.getTime() + 6 * 24 * 60 * 60 * 1000) // +6 días
        break
      case 'mes':
        fechaEntrada = hoy
        fechaSalida = new Date(hoy.getTime() + 29 * 24 * 60 * 60 * 1000) // +29 días (30 noches totales)
        break
      case 'rango':
        // ✅ VALIDACIÓN ESTRICTA: Solo consultar cuando AMBAS fechas están completas y válidas
        const regexFecha = /^\d{4}-\d{2}-\d{2}$/

        // 1. Validar que existan
        if (!fechaInicioPersonalizada || !fechaFinPersonalizada) {
          return null // No consultar si faltan fechas
        }

        // 2. Validar formato completo (evita consultas mientras se digita)
        if (!regexFecha.test(fechaInicioPersonalizada) || !regexFecha.test(fechaFinPersonalizada)) {
          return null // No consultar si las fechas no están completas
        }

        // 3. Validar que sean fechas reales parseables
        // ✅ CRÍTICO: Usar parseLocalDateString() para evitar timezone shift
        // new Date("2026-01-03") interpreta como UTC → shift de timezone
        // parseLocalDateString("2026-01-03") interpreta como LOCAL → sin shift
        const inicio = parseLocalDateString(fechaInicioPersonalizada)
        const fin = parseLocalDateString(fechaFinPersonalizada)
        if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) {
          return null // No consultar si las fechas son inválidas
        }

        // 4. Validar lógica de negocio: fecha_fin >= fecha_inicio
        if (inicio > fin) {
          return null // No consultar si fecha inicio > fecha fin
        }

        // 5. Formatear con formatLocalDateToString() para consistencia
        return {
          fecha_entrada: formatLocalDateToString(inicio),
          fecha_salida: formatLocalDateToString(fin)
        }
      default:
        return null
    }

    return {
      fecha_entrada: formatLocalDateToString(fechaEntrada),
      fecha_salida: formatLocalDateToString(fechaSalida)
    }
  }, [rangoSeleccionado, fechaInicioPersonalizada, fechaFinPersonalizada])

  // ✅ Obtener TODOS los espacios con disponibilidad marcada (panorámica)
  const { data: espaciosRaw = [], isLoading: loadingDisponibilidad, error: errorDisponibilidad } = useOcupacionPanoramica(filtrosDisponibilidad || undefined)

  // ✅ Filtrar espacios privados
  const espacios = useMemo(() => {
    return filterEspaciosPublicos(espaciosRaw)
  }, [espaciosRaw])

  const handleCampingClick = () => {
    setIsTransitioning(true)
    
    // Transición más larga y suave con múltiples pasos
    setTimeout(() => {
      setCurrentView('detailed')
      // Dar más tiempo para que el nuevo componente se monte completamente
      setTimeout(() => {
        setIsTransitioning(false)
      }, 400)
    }, 600) // Transición más lenta
  }

  const handleBackToLocation = () => {
    setIsTransitioning(true)

    setTimeout(() => {
      setCurrentView('google')
      setTimeout(() => {
        setIsTransitioning(false)
      }, 400)
    }, 600)
  }

  // URL del plano PNG
  const planoImageUrl = `${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`

  // Manejar click en espacio del plano - Navegar directo a detalle del espacio con fechas
  const handleFloorPlanReserve = (espacio: any) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    // Si hay fechas válidas seleccionadas y tipo_nombre, ir directo a detalle con fechas prellenadas
    if (filtrosDisponibilidad && espacio.tipo_nombre) {
      const tipoSlug = slugify(espacio.tipo_nombre)
      navigate(`/reservar/${tipoSlug}/${espacio.id}`, {
        state: {
          fechaEntrada: filtrosDisponibilidad.fecha_entrada,
          fechaSalida: filtrosDisponibilidad.fecha_salida
        }
      })
    } else {
      // Fallback: sin fechas, ir a página de tipos
      navigate('/reservar', { state: { selectedEspacioId: espacio.id } })
    }
  }

  return (
    <div className={`${className}`}>
      {/* Información del camping */}
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-bold text-camping-800 mb-2">
          🏕️ Camping Playa Niklitschek
        </h2>
      </div>

      {/* Control de disponibilidad: Botón + Chips */}
      <div className="mb-6 flex flex-col items-center gap-3">
        {/* Botón para activar/desactivar DIBUJO de indicadores en el plano */}
        <button
          onClick={() => setMostrarDisponibilidad(!mostrarDisponibilidad)}
          style={{
            backgroundColor: mostrarDisponibilidad ? colors.primary : '#FFFFFF',
            borderColor: colors.primary,
            color: mostrarDisponibilidad ? '#FFFFFF' : colors.primary
          }}
          className="font-bold text-lg px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-2xl hover:scale-105 active:scale-95 flex items-center justify-center gap-3 border-2"
          onMouseEnter={(e) => {
            if (mostrarDisponibilidad) {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            } else {
              e.currentTarget.style.backgroundColor = `${colors.primary}10`
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = mostrarDisponibilidad ? colors.primary : '#FFFFFF'
          }}
        >
          <span className="text-2xl">📅</span>
          <span>{mostrarDisponibilidad ? 'Ocultar' : 'Ver'} Disponibilidad</span>
        </button>

        {/* Chips para seleccionar rango (solo visibles cuando está activo el checkbox) */}
        {mostrarDisponibilidad && (
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap justify-center gap-2">
              {(['hoy', 'semana', 'mes', 'rango'] as RangoDisponibilidad[]).map((rango) => (
                <button
                  key={rango}
                  onClick={() => setRangoSeleccionado(rango)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    rangoSeleccionado === rango
                      ? 'text-white shadow-md scale-105'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                  }`}
                  style={{
                    backgroundColor: rangoSeleccionado === rango ? colors.primary : undefined
                  }}
                >
                  {rango === 'hoy' && 'Hoy'}
                  {rango === 'semana' && 'Esta semana'}
                  {rango === 'mes' && 'Este mes'}
                  {rango === 'rango' && 'Un rango'}
                </button>
              ))}
            </div>

            {/* Inputs de fechas personalizadas (solo si se selecciona "Un rango") */}
            {rangoSeleccionado === 'rango' && (
              <div className="flex flex-col sm:flex-row gap-2 items-center bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Inicio:</label>
                  <input
                    type="date"
                    value={fechaInicioPersonalizada}
                    onChange={(e) => setFechaInicioPersonalizada(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Fin:</label>
                  <input
                    type="date"
                    value={fechaFinPersonalizada}
                    min={fechaInicioPersonalizada}
                    onChange={(e) => setFechaFinPersonalizada(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            )}

            {/* Indicador de carga sutil */}
            {loadingDisponibilidad && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <div className="animate-spin w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                <span>Actualizando disponibilidad...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contenedor del mapa con transición mejorada */}
      <div className="relative overflow-hidden rounded-xl">
        {/* Overlay de transición mejorado */}
        {isTransitioning && (
          <div className="absolute inset-0 z-30 bg-gradient-to-br from-white via-blue-50 to-camping-50 flex items-center justify-center">
            <div className="text-center">
              <div className="relative mb-6">
                <div className="animate-spin w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl animate-pulse">
                    {currentView === 'google' ? '🗺️' : '🌍'}
                  </span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {currentView === 'google' ? 'Activando Plano Detallado' : 'Cargando Vista Satelital'}
              </h3>
              <p className="text-gray-600">
                {currentView === 'google'
                  ? 'Posicionando espacios con sistema de cuadrícula...'
                  : 'Conectando con Google Maps...'
                }
              </p>
              <div className="mt-4 flex justify-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Vista Google Maps */}
        <div className={`transition-all duration-700 ease-in-out transform ${
          currentView === 'google' && !isTransitioning
            ? 'opacity-100 scale-100 translate-x-0'
            : currentView === 'google' && isTransitioning
              ? 'opacity-50 scale-95 -translate-x-4'
              : 'opacity-0 scale-90 -translate-x-8 absolute inset-0'
        }`}>
          {currentView === 'google' && (
            <>
              <SimplifiedGoogleMap
                height={height}
                onCampingClick={handleCampingClick}
                className="shadow-xl rounded-xl border border-gray-200"
              />
            </>
          )}
        </div>

        {/* Vista Plano Detallado */}
        <div className={`transition-all duration-700 ease-in-out transform relative ${
          currentView === 'detailed' && !isTransitioning
            ? 'opacity-100 scale-100 translate-x-0'
            : currentView === 'detailed' && isTransitioning
              ? 'opacity-50 scale-95 translate-x-4'
              : 'opacity-0 scale-90 translate-x-8 absolute inset-0'
        }`}>
          {currentView === 'detailed' && (
              <>
                <div className="relative">
                  <CampingFloorPlan
                    espacios={espacios}
                    planoImageUrl={planoImageUrl}
                    onReserve={handleFloorPlanReserve}
                    searchQuery={null}
                    className="shadow-xl rounded-xl border border-gray-200"
                    showAvailabilityIndicators={mostrarDisponibilidad && !(rangoSeleccionado === 'rango' && !filtrosDisponibilidad)}
                  />
                  {/* Botón para volver a la vista satelital */}
                  <button
                    onClick={handleBackToLocation}
                    style={{
                      backgroundColor: colors.primary,
                      color: '#FFFFFF'
                    }}
                    className={`absolute top-4 right-4 rounded-lg shadow-lg hover:opacity-90 transition-all duration-200 font-medium flex items-center justify-center hover:scale-105 z-10 ${
                      deviceInfo.isMobile
                        ? 'w-10 h-10 text-xl p-0'
                        : 'px-4 py-2 text-sm space-x-2'
                    }`}
                    title={deviceInfo.isMobile ? 'Ver Mapa Satelital' : undefined}
                  >
                    <span>🌍</span>
                    {!deviceInfo.isMobile && <span>Ver Mapa Satelital</span>}
                  </button>
                </div>
              </>
          )}
        </div>
      </div>

    </div>
  )
}

export default TransitionMapSystemWithAPI
